import { z } from 'zod';
import type { Actor } from './actor';
import { assertNever } from './exhaustive';
import {
  type AggregateOp,
  aggregateOpSchema,
  type FilterOperator,
  filterOperatorSchema,
} from './field-types';
import {
  type FilterCombinator,
  type FilterNode,
  filterCombinatorSchema,
  type RelationCount,
  type RelationDirection,
  type RelationQuantifier,
  relationCountSchema,
  relationDirectionSchema,
  relationQuantifierSchema,
} from './filter-ast';
import type { MetadataOrigin } from './ids';
import {
  type AggregateRequest,
  aggregateRequestSchema,
  GROUP_MAX_DIMENSIONS,
  type GroupDimension,
  groupDimensionSchema,
} from './record-group';
import { type OrderBy, orderBySchema } from './record-query';

/**
 * SAVED VIEWS (record-page-and-views.md D14-D19).
 *
 * A view is a named, shared, per-object list configuration: filter, sort, columns,
 * table-or-board, and which SELECT field a board groups by. One shared set, no
 * `userId` (D14, INV-15).
 *
 * THE ONE THING A VIEW ADDS TO THE FILTER LANGUAGE IS RELATIVE DATES (D17), and it
 * adds them ABOVE the AST rather than inside it. INV-6 says there is one filter AST,
 * one SQL compiler and one JS evaluator; a `due_date this_week` operator would put
 * "now" inside a language whose two backends run on two machines whose clocks and
 * timezones do not agree. So a view stores a `ViewFilterNode`, which is the AST plus
 * one extra operand shape, and `materialiseViewFilter` turns it into a plain
 * `FilterNode` before anything executes it. Nothing downstream of that function ever
 * sees a token.
 */

export const SAVED_VIEW_MODES = ['table', 'board'] as const;
export type SavedViewMode = (typeof SAVED_VIEW_MODES)[number];
export const savedViewModeSchema = z.literal([...SAVED_VIEW_MODES]);

/**
 * The token set, CLOSED (D17). Every one of them is day-granular, which is what makes
 * materialisation a pair of `YYYY-MM-DD` bounds and not a timezone decision nobody has
 * taken (open risk 6).
 */
export const RELATIVE_DATE_TOKENS = [
  'today',
  'this_week',
  'next_7_days',
  'this_month',
  'last_30_days',
] as const;
export type RelativeDateToken = (typeof RELATIVE_DATE_TOKENS)[number];
export const relativeDateTokenSchema = z.literal([...RELATIVE_DATE_TOKENS]);

/** A DATE condition's operand, resolved against a clock rather than stored as a value. */
export interface RelativeDateOperand {
  readonly relative: RelativeDateToken;
}

export const relativeDateOperandSchema = z.strictObject({ relative: relativeDateTokenSchema });

export interface ViewFieldCondition {
  readonly kind: 'condition';
  readonly fieldId: string;
  readonly operator: FilterOperator;
  /** Absent for nullary operators; a `RelativeDateOperand` for a relative date. */
  readonly value?: unknown;
}

export interface ViewFilterGroup {
  readonly kind: 'group';
  readonly combinator: FilterCombinator;
  readonly children: readonly ViewFilterNode[];
  readonly negate?: boolean | undefined;
}

/**
 * The stored twin of `RelatedCondition` (D1). Its children are `ViewFilterNode`s, so a
 * relative date inside a related hop is stored as a token like any other.
 */
export interface ViewRelatedCondition {
  readonly kind: 'related';
  readonly anchorFieldId: string;
  readonly quantifier: RelationQuantifier;
  readonly direction?: RelationDirection | undefined;
  readonly count?: RelationCount | undefined;
  readonly children: readonly ViewFilterNode[];
}

export type ViewFilterNode = ViewFieldCondition | ViewFilterGroup | ViewRelatedCondition;

const viewConditionSchema = z.strictObject({
  kind: z.literal('condition'),
  fieldId: z.uuid(),
  operator: filterOperatorSchema,
  value: z.unknown().optional(),
});

export const viewFilterNodeSchema: z.ZodType<ViewFilterNode> = z.lazy(() =>
  z.discriminatedUnion('kind', [viewConditionSchema, viewGroupSchema, viewRelatedSchema]),
);

const viewGroupSchema = z.strictObject({
  kind: z.literal('group'),
  combinator: filterCombinatorSchema,
  children: z.array(viewFilterNodeSchema),
  negate: z.boolean().optional(),
});

const viewRelatedSchema = z.strictObject({
  kind: z.literal('related'),
  anchorFieldId: z.uuid(),
  quantifier: relationQuantifierSchema,
  direction: relationDirectionSchema.optional(),
  count: relationCountSchema.optional(),
  children: z.array(viewFilterNodeSchema),
});

/**
 * A stored operand IS a relative date. Structural, because `FieldCondition.value` is
 * `unknown` on both sides of the boundary and a jsonb round trip returns a plain object.
 */
export function isRelativeDateOperand(value: unknown): value is RelativeDateOperand {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = (value as { relative?: unknown }).relative;
  return (
    typeof candidate === 'string' && (RELATIVE_DATE_TOKENS as readonly string[]).includes(candidate)
  );
}

/**
 * THE ONE OPERATOR A RELATIVE DATE MAY CARRY.
 *
 * Every token denotes a SPAN - even `today`, which is one day wide - so `between` is
 * the only operator whose operand shape a span fits. Allowing `eq` as well would mean
 * two materialisation rules and one of them would have to pick a day out of a range,
 * which is a decision the token deliberately does not make.
 */
export const RELATIVE_DATE_OPERATOR: FilterOperator = 'between';

export interface RelativeDateRange {
  readonly from: string;
  readonly to: string;
}

/** Local `YYYY-MM-DD`, never `toISOString()`: that would shift the day in half the world. */
function isoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calendar arithmetic, NOT millisecond arithmetic.
 *
 * `new Date(t + 7 * 86_400_000)` is off by an hour across a DST boundary and can
 * therefore land on the previous or the next day - so "next 7 days" would silently be
 * six or eight in March and October. The `Date(y, m, d + n)` constructor normalises
 * over month and year ends and re-resolves the offset, which is the whole point.
 */
function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Resolves ONE token against a clock. Exported so a caller can render "1 Aug - 7 Aug"
 * next to the filter without re-deriving the arithmetic.
 *
 * The week starts on MONDAY. This instance is German and its users' week does; a
 * Sunday-first week would put "this week" on Payments due tomorrow every Sunday
 * evening.
 */
export function resolveRelativeDate(token: RelativeDateToken, now: Date): RelativeDateRange {
  const today = startOfDay(now);
  switch (token) {
    case 'today':
      return { from: isoDate(today), to: isoDate(today) };
    case 'this_week': {
      // getDay(): 0 = Sunday, so Sunday is six days after this week's Monday.
      const offset = (today.getDay() + 6) % 7;
      const monday = addDays(today, -offset);
      return { from: isoDate(monday), to: isoDate(addDays(monday, 6)) };
    }
    case 'next_7_days':
      // Seven days INCLUDING today, so a Payments view opened on Monday covers Sunday.
      return { from: isoDate(today), to: isoDate(addDays(today, 6)) };
    case 'this_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      // Day 0 of the NEXT month is the last day of this one, leap years included.
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: isoDate(first), to: isoDate(last) };
    }
    case 'last_30_days':
      return { from: isoDate(addDays(today, -29)), to: isoDate(today) };
    default:
      return assertNever(token, 'resolveRelativeDate');
  }
}

/**
 * A `ViewFilterNode` -> the plain `FilterNode` both backends execute.
 *
 * PURE, and the only door between the two types. `now` is a parameter rather than a
 * `new Date()` inside, so the same view materialises deterministically in a test and
 * in the browser's timezone at read time (open risk 6's v1 recommendation).
 */
export function materialiseViewFilter(node: ViewFilterNode, now: Date): FilterNode {
  /**
   * THE RELATED ARM SPREADS THE NODE AND REPLACES ONLY `children`, and that is a
   * PROPERTY rather than a style preference.
   *
   * This function sits BETWEEN the canonicaliser and the required-iff check on the view
   * save path (`views/validate.ts`: checkAndNormalise -> materialiseViewFilter ->
   * validateFilterAgainstFields). The group arm below enumerates its members by hand; an
   * arm written in that style before `count` existed would drop `count` in flight, the
   * required-iff check would then see `quantifier: 'count'` with no comparison, and EVERY
   * legal count view would fail its own save. Spreading means the next optional member
   * cannot be lost the same way.
   */
  if (node.kind === 'related') {
    return { ...node, children: node.children.map((child) => materialiseViewFilter(child, now)) };
  }
  if (node.kind === 'group') {
    return {
      kind: 'group',
      combinator: node.combinator,
      children: node.children.map((child) => materialiseViewFilter(child, now)),
      ...(node.negate === undefined ? {} : { negate: node.negate }),
    };
  }

  if (!isRelativeDateOperand(node.value)) {
    return node.value === undefined
      ? { kind: 'condition', fieldId: node.fieldId, operator: node.operator }
      : { kind: 'condition', fieldId: node.fieldId, operator: node.operator, value: node.value };
  }

  const range = resolveRelativeDate(node.value.relative, now);
  return {
    kind: 'condition',
    fieldId: node.fieldId,
    // The token decides the operator, so a stored `eq` cannot produce a range operand
    // the normalizer would then reject deep inside a query.
    operator: RELATIVE_DATE_OPERATOR,
    value: [range.from, range.to],
  };
}

/** Every relative token a stored filter carries, in walk order. For tests and for D18. */
export function relativeDateTokensOf(node: ViewFilterNode): readonly RelativeDateToken[] {
  if (node.kind === 'group') return node.children.flatMap(relativeDateTokensOf);
  /**
   * NO ARM FOR `related`, BY DECISION (D19, D26 row 8). This function has no production
   * caller - the only references are its own definition and three assertions in
   * `view.test.ts` - so an arm here would be a mechanism with no consumer, which is the
   * rule this module applies against itself everywhere else. One line, the day it gains
   * one: recurse into `children`.
   */
  if (node.kind === 'related') return [];
  return isRelativeDateOperand(node.value) ? [node.value.relative] : [];
}

/**
 * ONE OPERATION PER COLUMN, keyed by field id - the keying `columnFieldIds` already uses
 * (`docs/decisions/group-by-aggregation.md` D18).
 *
 * A missing key is a column with no footer cell, which is different from a column whose
 * footer is `count`: the footer is opt-in per column, and a whole `<tfoot>` with nothing
 * in it is not a row anybody wants sticky at the bottom of a table.
 */
export type ViewColumnAggregates = Readonly<Record<string, AggregateOp>>;

/**
 * A saved view on the wire. `Date` is an ISO string, exactly like every other read in
 * this API; `objectId` is the metadata UUID (INV-7), never the object's name.
 *
 * THE FOUR GROUPING MEMBERS ARE PER-VIEW STATE (group-by-aggregation.md D15, migration
 * 0015), which is the strongest consensus in that module's whole survey: Twenty, Notion,
 * Airtable and Attio all keep grouping on the view and none of them puts it on the
 * object. `boardGroupFieldId` stays where it is rather than folding into `groupBy` - a
 * view has exactly one `mode`, so only one of the two is ever read, and folding them
 * would be a data migration over live rows to serve a rename.
 */
export interface SavedView {
  readonly id: string;
  readonly universalId: string;
  readonly origin: MetadataOrigin;
  readonly objectId: string;
  readonly name: string;
  readonly mode: SavedViewMode;
  readonly filter: ViewFilterNode | null;
  /**
   * D17: THE STORED FILTER DID NOT PARSE, and it is a boolean on the wire rather than an
   * inference.
   *
   * `meta.saved_view.filter` is unconstrained jsonb, so nothing stops a row written by a
   * NEWER version of this application - or by hand - from carrying a shape this version
   * has no walker for. Returning it unparsed would hand the old walkers a node they drop
   * silently, which is the widening this module exists to prevent, arriving through the
   * module itself. `filter: null` ALONE would be indistinguishable from "no filter" and
   * would widen for the same reason, so the flag travels with it and the list refuses to
   * run the view at all.
   *
   * Absent (rather than `false`) on a healthy view, so the wire shape of every view
   * written before this member existed is unchanged.
   */
  readonly filterDamaged?: boolean | undefined;
  readonly orderBy: readonly OrderBy[] | null;
  readonly columnFieldIds: readonly string[];
  readonly boardGroupFieldId: string | null;
  /** The TABLE's dimensions, in order. Null means the table is not grouped. */
  readonly groupBy: readonly GroupDimension[] | null;
  /** The one number beside each group header. Null means "show the row count" (D18). */
  readonly groupAggregate: AggregateRequest | null;
  /** The footer, one operation per column. Null means no footer at all (D18). */
  readonly columnAggregates: ViewColumnAggregates | null;
  /**
   * A CLIENT display setting that happens to persist (D29). It never reaches the server:
   * a `GROUP BY` emits no row for a group with no rows, so showing one is always a client
   * synthesis, and it is only honest where a CLOSED DOMAIN exists to synthesise from -
   * SELECT and BOOLEAN. For a RELATION or a date bucket the toggle is DISABLED rather than
   * hidden, and `views/validate.ts` refuses `true` beside either, so a view written by
   * hand cannot claim a state the UI cannot produce.
   *
   * Default false, so empty groups are SHOWN: the case that motivates the feature is a
   * pipeline where "Won: 0" is the interesting number.
   */
  readonly hideEmptyGroups: boolean;
  readonly isDefault: boolean;
  readonly position: number;
  readonly createdBy: Actor;
  readonly updatedBy: Actor | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * NAME LENGTH, and it is enforced rather than assumed: the name is the tab label and
 * the partial unique index's key, so an unbounded one is both a layout problem and an
 * index-size one.
 */
export const SAVED_VIEW_NAME_MAX = 120;

/**
 * How many columns a view may pin. Generous next to the widest seeded object
 * (twenty-seven fields on `agency`) and small enough that the jsonb array stays a
 * list rather than a payload.
 */
export const SAVED_VIEW_MAX_COLUMNS = 100;

/**
 * THE FOUR GROUPING MEMBERS ON THE WIRE (group-by-aggregation.md D15, D18, D29).
 *
 * Written out once and spread into both write schemas, so a member cannot be added to one
 * and forgotten on the other - which is the shape `columnFieldIds` already has, restated
 * twice because zod has no cheaper way to say it.
 *
 * The type-dependent halves are NOT here and cannot be: whether a dimension's field is
 * groupable, whether its granularity belongs to its type and whether an operation is legal
 * for a field's type are questions about METADATA, and they are answered in
 * `views/validate.ts` where the field list is (D15).
 */
const groupingWriteMembers = {
  groupBy: z.array(groupDimensionSchema).min(1).max(GROUP_MAX_DIMENSIONS).nullable().optional(),
  groupAggregate: aggregateRequestSchema.nullable().optional(),
  columnAggregates: z.record(z.uuid(), aggregateOpSchema).nullable().optional(),
  hideEmptyGroups: z.boolean().optional(),
};

export const savedViewWriteSchema = z.strictObject({
  objectId: z.uuid(),
  name: z.string().min(1).max(SAVED_VIEW_NAME_MAX),
  mode: savedViewModeSchema,
  filter: viewFilterNodeSchema.nullable().optional(),
  orderBy: z.array(orderBySchema).nullable().optional(),
  columnFieldIds: z.array(z.uuid()).max(SAVED_VIEW_MAX_COLUMNS),
  boardGroupFieldId: z.uuid().nullable().optional(),
  ...groupingWriteMembers,
  isDefault: z.boolean().optional(),
});

export type SavedViewWriteInput = z.infer<typeof savedViewWriteSchema>;

/**
 * PATCH: every field optional, and `objectId` is NOT among them. Moving a view to
 * another object would invalidate its columns, its filter, its sort and its board
 * group field all at once - that is a new view, not an edit.
 */
export const savedViewPatchSchema = z.strictObject({
  name: z.string().min(1).max(SAVED_VIEW_NAME_MAX).optional(),
  mode: savedViewModeSchema.optional(),
  filter: viewFilterNodeSchema.nullable().optional(),
  orderBy: z.array(orderBySchema).nullable().optional(),
  columnFieldIds: z.array(z.uuid()).max(SAVED_VIEW_MAX_COLUMNS).optional(),
  boardGroupFieldId: z.uuid().nullable().optional(),
  ...groupingWriteMembers,
  isDefault: z.boolean().optional(),
  position: z.number().finite().optional(),
});

export type SavedViewPatchInput = z.infer<typeof savedViewPatchSchema>;
